import {
    useContext,
    type FC
} from "react";
import {
    ScrollView,
    View
} from "react-native";
import type IChartTableProps from "./type";
import stylesheet, {
    getChartTableCellStyle,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    getChartTableModel
} from "../../util";
import {
    ChartContext
} from "../../context";
import Text from "../../../text";

const ChartTable: FC<IChartTableProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    columns: columnsProp,
    rows: rowsProp,
    caption,
    style,
    ...props
}) => {
    const context = useContext(ChartContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        controlHeights,
        shapeConfigs,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalizeProp ?? context?.customLocalize);

    const model = context?.modelRef.current;

    const tableModel = model && context ? getChartTableModel({
        totalTitle: localize("chart-total"),
        config: context.config,
        model
    }) : undefined;

    const columns = columnsProp ?? tableModel?.columns ?? [];

    const rows = rowsProp ?? tableModel?.rows ?? [];

    const {
        caption: captionDynamicStyle
    } = useStyles({
        controlHeights,
        shapeConfigs,
        borders,
        colors,
        spaces
    });

    const renderCell = ({
        isLastRow,
        isHeader,
        column,
        text
    }: {
        isLastRow: boolean;
        isHeader: boolean;
        column: number;
        text: string;
    }) => {
        const isFirstColumn = column === 0;

        return <View
            role={isHeader ? "columnheader" : isFirstColumn ? "rowheader" : "cell"}
            style={[
                stylesheet.cell,
                isFirstColumn ? stylesheet.firstCell : stylesheet.numberCell,
                getChartTableCellStyle({
                    isLastColumn: column === columns.length - 1,
                    controlHeights,
                    isFirstColumn,
                    shapeConfigs,
                    isLastRow,
                    isHeader,
                    borders,
                    colors,
                    spaces
                })
            ]}
            key={column}
        >
            <Text
                variant={isHeader ? "labelSmallSize" : "bodyMediumSize"}
                color={isHeader ? "low" : "high"}
                customTheme={customTheme}
                numberOfLines={1}
            >
                {text}
            </Text>
        </View>;
    };

    return <View
        {...props}
        id={context ? `${context.baseID}-table` : props.id}
        style={[
            style,
            stylesheet.scroll
        ]}
    >
        {
            caption ?
                <Text
                    style={captionDynamicStyle}
                    customTheme={customTheme}
                    variant="bodySmallSize"
                    color="low"
                >
                    {caption}
                </Text>
            :
                null
        }
        <ScrollView
            contentContainerStyle={stylesheet.scrollContent}
            horizontal={true}
        >
            <View
                style={stylesheet.table}
                role="table"
            >
                <View
                    style={stylesheet.row}
                    role="row"
                >
                    {columns.map((text, column) => renderCell({
                        isLastRow: false,
                        isHeader: true,
                        column,
                        text
                    }))}
                </View>
                {
                    rows.map((cells, rowIndex) => <View
                        key={`${rowIndex}-${cells[0]}`}
                        style={stylesheet.row}
                        role="row"
                    >
                        {cells.map((text, column) => renderCell({
                            isLastRow: rowIndex === rows.length - 1,
                            isHeader: false,
                            column,
                            text
                        }))}
                    </View>)
                }
            </View>
        </ScrollView>
    </View>;
};
export default ChartTable;
